// CreatorBlitzPayRPAStatus.jsx
// Component to check and manage BlitzPay RPA status for creators in withdrawal dialog

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  AssignmentTurnedIn as AssignIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import API from '../../../../API';

export default function CreatorBlitzPayRPAStatus({ 
  creatorUsername, 
  creatorName,
  onStatusChange = () => {},
  showAssignButton = true 
}) {
  const [loading, setLoading] = useState(true);
  const [rpaStatus, setRpaStatus] = useState(null);
  const [error, setError] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  useEffect(() => {
    if (creatorUsername) {
      fetchRPAStatus();
    }
  }, [creatorUsername]);

  const fetchRPAStatus = async () => {
    if (!creatorUsername) return;

    setLoading(true);
    setError(null);

    try {
      const response = await API.contracts.getCreatorRPAStatus(creatorUsername);
      setRpaStatus(response);
      onStatusChange(response.rpa_status);
    } catch (err) {
      console.error('Error fetching RPA status:', err);
      setError(err?.response?.data?.error || 'Failed to fetch RPA status');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRPA = async () => {
    setAssigning(true);
    setError(null);

    try {
      const response = await API.contracts.assignRPAToCreator(creatorUsername, {
        send_notification: true,
        expires_in_days: 90
      });

      setAssignResult(response);
      setShowAssignDialog(true);
      
      // Refresh status after assignment
      await fetchRPAStatus();
    } catch (err) {
      console.error('Error assigning RPA:', err);
      setError(err?.response?.data?.error || 'Failed to assign RPA');
    } finally {
      setAssigning(false);
    }
  };

  const handleResendNotification = async () => {
    if (!rpaStatus?.signature_details?.id) return;

    setAssigning(true);
    setError(null);

    try {
      const response = await API.contracts.resendRPANotification(rpaStatus.signature_details.id);
      setAssignResult(response);
      setShowAssignDialog(true);
    } catch (err) {
      console.error('Error resending notification:', err);
      setError(err?.response?.data?.error || 'Failed to resend notification');
    } finally {
      setAssigning(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader title="BlitzPay RPA Status" />
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={32} />
        </CardContent>
      </Card>
    );
  }

  if (error && !rpaStatus) {
    return (
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader title="BlitzPay RPA Status" />
        <CardContent>
          <Alert severity="error" icon={<ErrorIcon />}>
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const hasSigned = rpaStatus?.rpa_status?.has_signed;
  const signedAt = rpaStatus?.rpa_status?.signed_at;
  const signatureType = rpaStatus?.rpa_status?.signature_type;
  const publicUrl = rpaStatus?.signature_details?.public_url;

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardHeader 
          title="BlitzPay RPA Compliance"
          action={
            <IconButton onClick={fetchRPAStatus} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          }
        />
        <CardContent>
          {/* Status Display */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {hasSigned ? (
              <>
                <Chip 
                  icon={<CheckCircleIcon />} 
                  label="RPA Signed" 
                  color="success" 
                  variant="outlined" 
                />
                <Typography variant="body2" color="text.secondary">
                  Signed {signedAt ? new Date(signedAt).toLocaleDateString() : 'recently'}
                  {signatureType && ` (${signatureType})`}
                </Typography>
              </>
            ) : (
              <>
                <Chip 
                  icon={<WarningIcon />} 
                  label="RPA Not Signed" 
                  color="warning" 
                  variant="outlined" 
                />
                <Typography variant="body2" color="text.secondary">
                  {publicUrl ? 'RPA assigned but not yet signed' : 'No RPA assignment found'}
                </Typography>
              </>
            )}
          </Box>

          {/* Creator Info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Creator: {creatorName || creatorUsername}
            </Typography>
            {rpaStatus?.creator_info && (
              <Typography variant="caption" display="block" color="text.secondary">
                Email: {rpaStatus.creator_info.payout_email || rpaStatus.creator_info.email || 'N/A'}
              </Typography>
            )}
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Action based on status */}
          {!hasSigned && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!publicUrl && showAssignButton ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={assigning ? <CircularProgress size={16} /> : <AssignIcon />}
                  onClick={handleAssignRPA}
                  disabled={assigning}
                >
                  Assign BlitzPay RPA
                </Button>
              ) : publicUrl ? (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={handleResendNotification}
                    disabled={assigning}
                  >
                    Resend Request
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<OpenIcon />}
                    onClick={() => window.open(publicUrl, '_blank')}
                  >
                    Open RPA Link
                  </Button>
                  <Tooltip title="Copy link to clipboard">
                    <IconButton 
                      onClick={() => copyToClipboard(publicUrl)}
                      size="small"
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              ) : null}
            </Box>
          )}

          {hasSigned && rpaStatus?.signature_details && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="body2">
                <strong>BlitzPay RPA is signed and compliant.</strong>
                {rpaStatus.signature_details.signed_at && (
                  <> Signed on {new Date(rpaStatus.signature_details.signed_at).toLocaleString()}.</>
                )}
              </Typography>
            </Alert>
          )}

          {/* Warning for unsigned RPA */}
          {!hasSigned && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Payment Hold:</strong> This withdrawal may be held until the creator signs the BlitzPay RPA.
                {publicUrl ? ' A request has been sent to the creator.' : ' Assign the RPA to proceed with payment processing.'}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Assignment Result Dialog */}
      <Dialog 
        open={showAssignDialog} 
        onClose={() => setShowAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {assignResult?.already_signed ? 'Already Signed' : 'RPA Assigned Successfully'}
        </DialogTitle>
        <DialogContent>
          {assignResult?.already_signed ? (
            <Alert severity="info">
              The creator has already signed the BlitzPay RPA.
            </Alert>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                BlitzPay RPA has been assigned to {creatorName || creatorUsername}.
              </Alert>
              
              {assignResult?.public_url && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>Public Signing Link:</strong>
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'grey.100', 
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Link 
                      href={assignResult.public_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ 
                        wordBreak: 'break-all',
                        flex: 1,
                        fontSize: '0.875rem'
                      }}
                    >
                      {assignResult.public_url}
                    </Link>
                    <IconButton 
                      size="small"
                      onClick={() => copyToClipboard(assignResult.public_url)}
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {assignResult?.expires_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Link expires: {new Date(assignResult.expires_at).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAssignDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}